import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
  EyeIcon,
  XMarkIcon,
  ChartBarIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { apiClient } from '../lib/apiClient';
import InsightDetailModal from '../components/InsightDetailModal';
import { WeeklyDigestModal } from '../components/WeeklyDigestModal';

interface Insight {
  id: string;
  type: 'churn_risk' | 'expansion_opportunity' | 'attention_needed' | 'positive_trend';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  customer_id: string;
  customer_name: string;
  created_at: string;
  status: 'active' | 'viewed' | 'dismissed' | 'resolved';
  source: string;
}

interface AnalyticsSummary {
  total_insights: number;
  active_insights: number;
  by_type: {
    churn_risk: number;
    expansion_opportunity: number;
    attention_needed: number;
    positive_trend: number;
  };
  by_severity: {
    high: number;
    medium: number;
    low: number;
  };
  by_status: {
    active: number;
    viewed: number;
    dismissed: number;
    resolved: number;
  };
  health_distribution: {
    excellent: number;
    good: number;
    at_risk: number;
    critical: number;
    total_customers: number;
    average_health: number;
  };
}

const AnalyticsPage: React.FC = () => {
  const location = useLocation();
  const [filters, setFilters] = useState({
    insight_type: '',
    severity: '',
    status: '',
    customer_id: ''
  });
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [showDigestModal, setShowDigestModal] = useState(false);
  const [digestData, setDigestData] = useState<any>(null);
  const [isGeneratingDigest, setIsGeneratingDigest] = useState(false);
  const hasProcessedNavigationState = useRef(false);
  
  const queryClient = useQueryClient();

  // Fetch analytics insights
  const { data: insightsData, isLoading: insightsLoading, error: insightsError } = useQuery({
    queryKey: ['analytics-insights', filters],
    queryFn: () => apiClient.getAnalyticsInsights(filters)
    // Removed refetchInterval to stop wasteful polling
  });

  // Fetch analytics summary
  const { data: summaryData, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: ['analytics-summary'],
    queryFn: () => apiClient.getAnalyticsSummary()
    // Removed refetchInterval to stop wasteful polling
  });

  // Fetch customers for filter dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => apiClient.getCustomers({ limit: 100 }), // Get more customers for the dropdown
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Update insight status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ insightId, status }: { insightId: string; status: string }) =>
      apiClient.updateInsightStatus(insightId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analytics-insights'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-summary'] });
    }
  });

  // Trigger daily batch analytics mutation
  const triggerAnalyticsMutation = useMutation({
    mutationFn: () => apiClient.triggerDailyAnalytics(),
    onSuccess: () => {
      // Refresh all analytics data after successful trigger
      queryClient.invalidateQueries({ queryKey: ['analytics-insights'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-summary'] });
      alert('Daily analytics processing triggered successfully! Data will be updated shortly.');
    },
    onError: (error) => {
      alert(`Failed to trigger analytics: ${error.message}`);
    }
  });

  // Handle navigation state from dashboard
  useEffect(() => {
    const state = location.state as { highlightInsight?: string; customerFilter?: string } | null;
    if (state?.customerFilter) {
      setFilters(prev => ({ ...prev, customer_id: state.customerFilter! }));
    }
  }, [location.state]);

  // Auto-open insight modal when navigating from dashboard
  useEffect(() => {
    const state = location.state as { highlightInsight?: string } | null;
    if (state?.highlightInsight && insightsData?.insights && !showInsightModal && !hasProcessedNavigationState.current) {
      console.log('Auto-opening modal for insight:', state.highlightInsight);
      hasProcessedNavigationState.current = true; // Mark as processed
      const insight = insightsData.insights.find((i: Insight) => i.id === state.highlightInsight);
      if (insight) {
        setSelectedInsight(insight);
        setShowInsightModal(true);
        // Update status to 'viewed' when auto-opening (only once)
        if (insight.status !== 'viewed') {
          updateStatusMutation.mutate({ insightId: insight.id, status: 'viewed' });
        }
      }
    }
  }, [insightsData, location.state]);

  const insights: Insight[] = insightsData?.insights || [];
  const summary: AnalyticsSummary = summaryData || {
    total_insights: 0,
    active_insights: 0,
    by_type: { churn_risk: 0, expansion_opportunity: 0, attention_needed: 0, positive_trend: 0 },
    by_severity: { high: 0, medium: 0, low: 0 },
    by_status: { active: 0, viewed: 0, dismissed: 0, resolved: 0 },
    health_distribution: { excellent: 0, good: 0, at_risk: 0, critical: 0, total_customers: 0, average_health: 0 }
  };

  // Ensure summary has the expected structure
  const safeSummary = {
    total_insights: summary?.total_insights || 0,
    active_insights: summary?.active_insights || 0,
    by_type: {
      churn_risk: summary?.by_type?.churn_risk || 0,
      expansion_opportunity: summary?.by_type?.expansion_opportunity || 0,
      attention_needed: summary?.by_type?.attention_needed || 0,
      positive_trend: summary?.by_type?.positive_trend || 0
    },
    by_severity: {
      high: summary?.by_severity?.high || 0,
      medium: summary?.by_severity?.medium || 0,
      low: summary?.by_severity?.low || 0
    },
    by_status: {
      active: summary?.by_status?.active || 0,
      viewed: summary?.by_status?.viewed || 0,
      dismissed: summary?.by_status?.dismissed || 0,
      resolved: summary?.by_status?.resolved || 0
    },
    health_distribution: {
      excellent: summary?.health_distribution?.excellent || 0,
      good: summary?.health_distribution?.good || 0,
      at_risk: summary?.health_distribution?.at_risk || 0,
      critical: summary?.health_distribution?.critical || 0,
      total_customers: summary?.health_distribution?.total_customers || 0,
      average_health: summary?.health_distribution?.average_health || 0
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'churn_risk':
        return <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />;
      case 'expansion_opportunity':
        return <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />;
      case 'attention_needed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'positive_trend':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      default:
        return <ChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      high: 'bg-destructive/10 text-destructive border-destructive/20',
      medium: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      low: 'bg-green-500/10 text-green-600 border-green-500/20'
    };
    return colors[severity as keyof typeof colors] || 'bg-muted text-muted-foreground border-border';
  };

  const getTypeBadge = (type: string) => {
    const colors = {
      churn_risk: 'bg-destructive/10 text-destructive border-destructive/20',
      expansion_opportunity: 'bg-green-500/10 text-green-600 border-green-500/20',
      attention_needed: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      positive_trend: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors[type as keyof typeof colors] || 'bg-muted text-muted-foreground border-border';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = (insightId: string, status: string) => {
    console.log('Updating insight status:', insightId, 'to', status);
    updateStatusMutation.mutate({ insightId, status }, {
      onSuccess: () => {
        console.log('Status updated successfully, closing modal');
        setShowInsightModal(false);
        setSelectedInsight(null);
        hasProcessedNavigationState.current = false;
      },
      onError: (error) => {
        console.error('Failed to update status:', error);
      }
    });
  };

  const handleGenerateDigest = async () => {
    setIsGeneratingDigest(true);
    try {
      const response = await apiClient.post('/api/insights/weekly-digest', {});
      // Extract data from AxiosResponse
      const responseData = response.data;
      // Normalize the response to ensure arrays are always present
      const normalizedDigest = {
        ...responseData,
        at_risk_accounts: Array.isArray(responseData.at_risk_accounts) ? responseData.at_risk_accounts : [],
        thriving_accounts: Array.isArray(responseData.thriving_accounts) ? responseData.thriving_accounts : [],
        product_gaps: Array.isArray(responseData.product_gaps) ? responseData.product_gaps : [],
        summary: responseData.summary || '',
        start_date: responseData.start_date || '',
        end_date: responseData.end_date || '',
        generated_at: responseData.generated_at || new Date().toISOString()
      };
      setDigestData(normalizedDigest);
      setShowDigestModal(true);
    } catch (error) {
      console.error('Failed to generate weekly digest:', error);
      alert('Failed to generate weekly digest');
    } finally {
      setIsGeneratingDigest(false);
    }
  };

  const handleViewInsight = (insight: Insight) => {
    setSelectedInsight(insight);
    setShowInsightModal(true);
    // Also update status to 'viewed' when viewing
    updateStatusMutation.mutate({ insightId: insight.id, status: 'viewed' });
  };

  const handleCloseModal = () => {
    console.log('Closing modal...');
    setShowInsightModal(false);
    setSelectedInsight(null);
    hasProcessedNavigationState.current = false; // Reset the ref
    // Clear the navigation state to prevent re-opening
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  if (insightsLoading && summaryLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (insightsError || summaryError) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-foreground mb-4">Insights</h1>
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 max-w-md mx-auto">
              <h2 className="text-lg font-semibold text-destructive mb-2">Error Loading Insights</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {insightsError ? `Insights Error: ${insightsError.message}` : ''}
                {summaryError ? `Summary Error: ${summaryError.message}` : ''}
              </p>
              <Button 
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['analytics-insights'] });
                  queryClient.invalidateQueries({ queryKey: ['analytics-summary'] });
                }}
                variant="outline"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Insights</h1>
              <p className="text-sm sm:text-base text-muted-foreground">AI-powered insights and customer health analytics</p>
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={handleGenerateDigest}
                disabled={isGeneratingDigest}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
              >
                {isGeneratingDigest ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Generate Weekly Digest</span>
                <span className="sm:hidden">Digest</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ['analytics-insights'] });
                  queryClient.invalidateQueries({ queryKey: ['analytics-summary'] });
                }}
                disabled={insightsLoading || summaryLoading}
                className="flex items-center space-x-2"
              >
                <ArrowPathIcon className={`h-4 w-4 ${(insightsLoading || summaryLoading) ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh Data</span>
                <span className="sm:hidden">Refresh</span>
              </Button>
              <Button
                onClick={() => triggerAnalyticsMutation.mutate()}
                disabled={triggerAnalyticsMutation.isPending}
                className="flex items-center space-x-2"
              >
                <ArrowPathIcon className={`h-4 w-4 ${triggerAnalyticsMutation.isPending ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Run Analytics</span>
                <span className="sm:hidden">Run</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-foreground">{safeSummary.active_insights}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {safeSummary.total_insights} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Churn Risks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-destructive">{safeSummary.by_type.churn_risk}</div>
              <p className="text-xs text-muted-foreground mt-1">Require attention</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Expansion Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-green-600">{safeSummary.by_type.expansion_opportunity}</div>
              <p className="text-xs text-muted-foreground mt-1">Growth potential</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Health Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold text-primary">
                {safeSummary.health_distribution.average_health.toFixed(1)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {safeSummary.health_distribution.total_customers} customers
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Health Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Health Score Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-muted-foreground">Excellent (80-100)</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 sm:w-32 bg-muted rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full" 
                        style={{ 
                          width: `${safeSummary.health_distribution.total_customers ? (safeSummary.health_distribution.excellent / safeSummary.health_distribution.total_customers) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{safeSummary.health_distribution.excellent}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-muted-foreground">Good (60-79)</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 sm:w-32 bg-muted rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ 
                          width: `${safeSummary.health_distribution.total_customers ? (safeSummary.health_distribution.good / safeSummary.health_distribution.total_customers) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{safeSummary.health_distribution.good}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-muted-foreground">At Risk (40-59)</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 sm:w-32 bg-muted rounded-full h-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full" 
                        style={{ 
                          width: `${safeSummary.health_distribution.total_customers ? (safeSummary.health_distribution.at_risk / safeSummary.health_distribution.total_customers) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{safeSummary.health_distribution.at_risk}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm text-muted-foreground">Critical (0-39)</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-24 sm:w-32 bg-muted rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full" 
                        style={{ 
                          width: `${safeSummary.health_distribution.total_customers ? (safeSummary.health_distribution.critical / safeSummary.health_distribution.total_customers) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                    <span className="text-xs sm:text-sm font-medium text-foreground">{safeSummary.health_distribution.critical}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Insight Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <ArrowTrendingDownIcon className="h-4 w-4 text-destructive" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Churn Risk</span>
                  </div>
                  <Badge className={getSeverityBadge('high')}>
                    {safeSummary.by_type.churn_risk}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Expansion</span>
                  </div>
                  <Badge className={getSeverityBadge('low')}>
                    {safeSummary.by_type.expansion_opportunity}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Attention Needed</span>
                  </div>
                  <Badge className={getSeverityBadge('medium')}>
                    {safeSummary.by_type.attention_needed}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-4 w-4 text-primary" />
                    <span className="text-xs sm:text-sm text-muted-foreground">Positive Trends</span>
                  </div>
                  <Badge className={getSeverityBadge('low')}>
                    {safeSummary.by_type.positive_trend}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-4 sm:mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Filters</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Type</label>
                <select 
                  value={filters.insight_type} 
                  onChange={(e) => setFilters({...filters, insight_type: e.target.value})}
                  className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                >
                  <option value="">All Types</option>
                  <option value="churn_risk">Churn Risk</option>
                  <option value="expansion_opportunity">Expansion Opportunity</option>
                  <option value="attention_needed">Attention Needed</option>
                  <option value="positive_trend">Positive Trend</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Severity</label>
                <select 
                  value={filters.severity} 
                  onChange={(e) => setFilters({...filters, severity: e.target.value})}
                  className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                >
                  <option value="">All Severities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                <select 
                  value={filters.status} 
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="viewed">Viewed</option>
                  <option value="dismissed">Dismissed</option>
                  <option value="resolved">Resolved</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Customer</label>
                <select 
                  value={filters.customer_id} 
                  onChange={(e) => setFilters({...filters, customer_id: e.target.value})}
                  className="w-full border border-input rounded-lg px-3 py-2 bg-background text-foreground focus:ring-2 focus:ring-ring focus:border-ring text-sm"
                >
                  <option value="">All Customers</option>
                  {customersData?.customers?.map((customer: any) => (
                    <option key={customer.customer_id} value={customer.customer_id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insights List */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Insights</CardTitle>
          </CardHeader>
          <CardContent>
            {insights.length === 0 ? (
              <div className="text-center py-8">
                <ChartBarIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-medium text-foreground">No insights found</h3>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                  Try adjusting your filters or check back later for new analytics.
                </p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {insights.map((insight) => (
                  <div 
                    key={insight.id}
                    className="border border-border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow bg-card"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0 mt-0.5">
                          {getInsightIcon(insight.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 mb-2">
                            <h3 className="text-sm font-medium text-card-foreground truncate">{insight.title}</h3>
                            <div className="flex space-x-1">
                              <Badge className={`${getTypeBadge(insight.type)} text-xs`}>
                                {insight.type.replace('_', ' ')}
                              </Badge>
                              <Badge className={`${getSeverityBadge(insight.severity)} text-xs`}>
                                {insight.severity}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 line-clamp-2">{insight.description}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs text-muted-foreground">
                            <span className="truncate">{insight.customer_name}</span>
                            <span className="hidden sm:inline">{formatDate(insight.created_at)}</span>
                            <span className="hidden sm:inline">Source: {insight.source}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ml-2">
                        {insight.status === 'active' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewInsight(insight)}
                              disabled={updateStatusMutation.isPending}
                              className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3"
                            >
                              <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden sm:inline ml-1">View</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(insight.id, 'dismissed')}
                              disabled={updateStatusMutation.isPending}
                              className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3"
                            >
                              <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              <span className="hidden sm:inline ml-1">Dismiss</span>
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Insight Detail Modal */}
        <InsightDetailModal
          insight={selectedInsight}
          isOpen={showInsightModal}
          onClose={handleCloseModal}
          onStatusUpdate={handleStatusUpdate}
          isUpdating={updateStatusMutation.isPending}
        />

        {/* Weekly Digest Modal */}
        <WeeklyDigestModal
          isOpen={showDigestModal}
          onClose={() => setShowDigestModal(false)}
          digestData={digestData}
        />
      </div>
    </div>
  );
};

export default AnalyticsPage;
